import { fabric } from 'fabric';
import Vec2 from 'vec2';

import { VERSIONS } from '@common/constants';
import { SideItemType } from '@common/enums';
import { Estimate, ProductCatalogMap, ProductDetails, SelectedItemMap, SelectedItemProperties } from '@common/models';

/*********************************************************************************************
 * IMPORTANT!
 * ANY CHANGES MADE TO THESE FUNCTIONS MUST BE REPLICATED IN BOTH EE2 AND EE3 TO ENSURE
 * COMPATABILITY AND CONSISTENCY
 * *********************************************************************************************/

/**
 * identifies keys that are know URLs (ending in url)
 * @param key
 */
const isUrl = (key: string) => key.match(/url$/i);

type SerializedVec2 = { x: number; y: number };

type Maps = Map<number, SelectedItemMap | ProductCatalogMap>;
type Value = number[] | SerializedVec2 | SelectedItemProperties[] | ProductDetails[];
type RevivableObject = {
    dataType?: string;
    value: Value;
};

type RevivableValue = string | number | boolean | null | RevivableObject | object;

interface ReplacerOptions {
    saveVersion?: VERSIONS;
    mapReplacer: (key: string, value: Maps) => RevivableValue;
}

export const mapToObject = <K extends string | number | symbol, V>(map: Map<K, V>): Record<K, unknown> => {
    const obj = {} as Record<K, unknown>;

    if (!map) {
        return obj;
    }

    for (const [key, value] of map.entries()) {
        if (value instanceof Map) {
            obj[key] = mapToObject(value);
        } else {
            obj[key] = value;
        }
    }

    return obj;
};

/**
 * JSON replacer function to generate v2 style estimate data with MAPs
 * @param key
 * @param value
 */
export const replacerV2 = (key: string, value: unknown) => {
    const mapReplacer = (_key: string, value: Maps) => {
        return {
            dataType: 'Map',
            value: Array.from(value.entries()),
        } as unknown as RevivableObject;
    };
    return replacer(key, value, { saveVersion: VERSIONS.V2, mapReplacer });
};

/**
 * JSON replacer function to generate v3 style estimate data with Objects for Maps
 * @param key
 * @param value
 */
export const replacerV3 = (key: string, value: unknown) => {
    const mapReplacer = (_key: string, value: Maps): RevivableValue => {
        return mapToObject(value);
    };
    return replacer(key, value, { saveVersion: VERSIONS.V3, mapReplacer });
};

/**
 * JSON replacer function, generates specified version
 * @param key string key of the object being serialized
 * @param value any value to be serialized
 * @param saveVersion version number for the resulting json
 * @param mapReplacer optional function to replace Map objects
 */
export const replacer = (
    key: string,
    value: unknown,
    { saveVersion: _saveVersion, mapReplacer }: ReplacerOptions
): unknown => {
    if (!value) {
        return value;
    }

    if (value instanceof fabric.Object) {
        return undefined;
    }

    const skipTheseKeys = ['labelText', 'labelMeasure'];
    if (skipTheseKeys.includes(key)) {
        return undefined;
    }

    // force encoding of any URL values for safety
    if (isUrl(key)) {
        return encodeURI(value as string);
    }

    if (typeof value === 'object') {
        if (value instanceof Map) {
            return mapReplacer ? mapReplacer(key, value) : mapToObject(value); // safety valve, but should really never be null!
        }
        if (value instanceof Vec2 || (Object.keys(value).length === 2 && 'x' in value && 'y' in value)) {
            return {
                dataType: 'Vec2',
                value: [value.x, value.y],
            };
        }
    }
    return value;
};

/**
 * JSON reviver function to restore v2 or v3 style estimate data
 * V2 will contain Maps
 * V3 will contain Objects (for Maps)
 * @param key
 * @param value
 */
export const reviver = (key: string, value: RevivableValue): RevivableValue | Vec2 => {
    // force encoding of any URL values for safety
    if (isUrl(key) && value) {
        return decodeURI(value as string);
    }

    // we chaned the value of this enum entry, which won't display correctly if we don't fix it here
    if (key === 'sideItemType' && typeof value === 'string' && value.toLowerCase() === 'waterfall panel') {
        return SideItemType.WATERFALL.toString();
    }

    if (typeof value === 'object' && value !== null && 'dataType' in value) {
        if (value.dataType === 'Map') {
            return new Map(value.value as unknown as Maps);
        }
        if (value.dataType === 'Vec2') {
            return new Vec2(value.value as number[]);
        }
    }
    return value;
};

export const convertToVersion = (
    estimate: Estimate | undefined,
    toVersion: VERSIONS.V2 | VERSIONS.V3
): Estimate | undefined => {
    const mapKeys = [
        'cutOuts',
        'tearOuts',
        'chipMinimizers',
        'faucets',
        'optionalProducts',
        'plumbingItems',
        'productCatalog',
    ] as (keyof Estimate)[];
    const currentVersion = inferEstimateVersion(estimate);
    if (!estimate || currentVersion < VERSIONS.V2 || currentVersion === toVersion) {
        return estimate;
    }

    const converted: Partial<Estimate> = mapKeys.reduce((result: Partial<Estimate>, key: keyof Estimate) => {
        const isObject = typeof estimate[key] === 'object';
        const convertedItem =
            toVersion === VERSIONS.V2
                ? new Map(
                      Object.entries(isObject && estimate[key] ? (estimate[key] as object) : {}).map(([k, v]) => [
                          safeParseNumber(k), // force this since it's supposed to be a productId
                          v as unknown as SelectedItemMap | ProductCatalogMap,
                      ])
                  )
                : mapToObject(estimate[key] as Maps);

        return { ...result, [key]: convertedItem };
    }, {});

    return Object.assign({}, estimate, converted);
};

export const inferEstimateVersion = (estimateData: Estimate | never | undefined): VERSIONS => {
    //these are the known top-level keys for the EEV1 data structure, if any of them are in there, it's v1
    const isV1 = Object.keys(estimateData ?? {}).some((key) =>
        ['counterTopArr', 'floatingItems', 'floatingCookTopSplashItems'].includes(key)
    );
    if (isV1) {
        return VERSIONS.V1;
    }

    // blank data is OK, but it's not really versioned
    // if it's missing drawingShapes or lineItems, then it can't be a valid V2 or V3 estimate
    const hasRequiredData = !!estimateData?.drawingShapes && !!estimateData?.lineItems;
    if (!hasRequiredData) {
        return VERSIONS.UNKNOWN;
    }

    // if productCatalog (we could check others as well) is a Map, then it must be v2
    const hasMaps = estimateData?.productCatalog instanceof Map;
    return hasMaps ? VERSIONS.V2 : VERSIONS.V3;
};

export const safeParseNumber = (value: string): number | string => {
    // if it's not a string, just leave it alone
    if (!value || typeof value !== 'string') {
        return value;
    }

    // Use Number() for conversion and isFinite to verify it's a valid finite number
    const num = Number(value);

    // If it's not a valid number, just stick with the original string
    if (!Number.isFinite(num)) {
        return value;
    }

    return num;
};
